import React, { Suspense, lazy, useEffect, useState } from "react";
import { Download } from "lucide-react";
import { AppButton, AppModal } from "../../ui";
import Shimmer from "../../Common/Shimmer";
import { downloadPdf } from "../../Fees/invoice/pdfActions";

// react-pdf is heavy and only needed once someone opens the agreement.
const PdfView = lazy(() => import("../../Common/PdfView"));

/**
 * The agreement's bytes while `active`, read again when `reloadKey` changes. `error`
 * is the sentence to show. Shared with the student's agreement page, which shows the
 * same PDF in the page instead of a dialog.
 */
export const useAgreementBytes = (fetchBytes, active, reloadKey = 0) => {
  const [bytes, setBytes] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!active || !fetchBytes) return undefined;
    let live = true;
    setError("");
    fetchBytes()
      .then((b) => { if (live) setBytes(b); })
      .catch((err) => { if (live) setError(err?.response?.status === 404 ? "There is no agreement to show yet." : "The agreement could not be loaded."); });
    return () => { live = false; };
  }, [active, fetchBytes, reloadKey]);

  return { bytes, error };
};

/**
 * AgreementPdfModal — the agreement PDF as the server drew it, in a dialog,
 * with a download of the same bytes. `fetchBytes` is whichever door's read
 * (the staff route, the student's, or the emailed link's); `reloadKey`
 * fetches again after a move changed what the PDF says.
 */
const AgreementPdfModal = ({ show, onHide, fetchBytes, fileName = "agreement.pdf", title = "Placement agreement", reloadKey = 0, footer = null }) => {
  const { bytes, error } = useAgreementBytes(fetchBytes, show, reloadKey);

  return (
    <AppModal
      show={show}
      onHide={onHide}
      title={title}
      size="lg"
      footer={(
        <>
          {footer}
          <AppButton variant="outline" onClick={() => bytes && downloadPdf(bytes, fileName)} disabled={!bytes}>
            <Download size={16} /> Download PDF
          </AppButton>
          <AppButton onClick={onHide}>Close</AppButton>
        </>
      )}
    >
      <Suspense fallback={<Shimmer height={480} />}>
        <PdfView bytes={bytes} label={title} error={error} />
      </Suspense>
    </AppModal>
  );
};

export default AgreementPdfModal;
